import React from 'react';
import { createUseStyles } from 'react-jss';
import { useT } from '../../i18n';
import useModalEscape from '../../hooks/useModalEscape';
import scrollbar from '../../styles/scrollbar';
import RobotIcon from '../icons/RobotIcon';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

const ROBOT_COLORS = ['#d94f2b', '#2a63a8', '#e0a325', '#2f9e6e'];

/**
 * What a first-time visitor meets before the menu.
 *
 * Deliberately not a `MenuModal`: that shell carries a title bar and a bare
 * `✕`, and an unlabelled X is a third way out that explains nothing. This one
 * has exactly two exits and both of them say what they do — one leads to the
 * tutorial, the other admits to knowing the game. There is no backdrop
 * dismissal for the same reason: a stray click outside would file the visitor
 * as having declined something they never read.
 *
 * ESC is the exception, and stays: it is a deliberate keyboard gesture with an
 * app-wide contract (one Escape peels off one layer), so it goes through the
 * shared `useModalEscape` stack like every other modal and lands on Skip.
 *
 * It knows nothing about storage or routing — `Menu` decides who sees it and
 * what each answer costs.
 */
const useStyles = createUseStyles({
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    background: 'rgba(14,8,4,0.78)',
  },

  panel: {
    width: '100%',
    maxWidth: '420px',
    maxHeight: '86dvh',
    fallbacks: [{ maxHeight: '86vh' }],
    boxSizing: 'border-box',
    overflowY: 'auto',
    ...scrollbar,
    background: '#2a1d13',
    border: '1px solid #6b4622',
    borderRadius: '14px',
    padding: '24px 20px 20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '14px',
    textAlign: 'center',
    boxShadow: [
      'inset 0 1px 0 rgba(255,255,255,0.12)',
      'inset 0 -3px 0 rgba(0,0,0,0.4)',
      '0 24px 60px rgba(0,0,0,0.6)',
    ].join(','),
  },

  marks: { display: 'flex', gap: '8px' },

  mark: { width: '30px', height: '30px' },

  title: {
    margin: 0,
    color: '#f5e6c8',
    fontSize: '19px',
    fontWeight: 700,
    letterSpacing: '0.5px',
  },

  body: {
    margin: 0,
    color: '#c4a070',
    fontSize: '13.5px',
    lineHeight: 1.55,
  },

  // The one raised plate on the panel, matching the menu's Create button —
  // this is the thing the panel exists to offer.
  showBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    width: '100%',
    minHeight: '52px',
    marginTop: '4px',
    padding: '10px 14px',
    boxSizing: 'border-box',
    background: '#d94f2b',
    border: '1px solid #120c07',
    borderRadius: '9px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '14px',
    fontWeight: 700,
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    color: '#fff3e6',
    boxShadow: [
      'inset 0 1px 0 rgba(255,255,255,0.3)',
      'inset 0 -3px 0 rgba(0,0,0,0.28)',
      '0 3px 0 #7d2a12',
    ].join(','),
    transition: 'background 0.12s, transform 0.06s, box-shadow 0.06s',
    '&:hover': { background: '#e35c36' },
    '&:active': {
      transform: 'translateY(3px)',
      boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.55)',
    },
    '&:focus-visible': { outline: '2px solid #e35c36', outlineOffset: '2px' },
  },

  chevron: { fontSize: '17px', lineHeight: 1, color: 'rgba(255,243,230,0.7)' },

  skipBtn: {
    minHeight: '44px',
    padding: '0 14px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: '#8a6e4c',
    fontFamily: MONO,
    fontSize: '10.5px',
    fontWeight: 700,
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    transition: 'color .12s',
    '&:hover': { color: '#f5e6c8' },
    '&:focus-visible': { outline: '2px solid #e35c36', outlineOffset: '2px' },
  },
});

function WelcomePanel({ open, onShowMe, onSkip }) {
  const classes = useStyles();
  const t = useT();
  useModalEscape(open, onSkip);

  if (!open) return null;

  const title = t('menu.welcome.title');

  return (
    <div className={classes.overlay} role="dialog" aria-modal="true" aria-label={title}>
      <div className={classes.panel}>
        <div className={classes.marks} aria-hidden="true">
          {ROBOT_COLORS.map(c => (
            <div key={c} className={classes.mark}>
              <RobotIcon color={c} />
            </div>
          ))}
        </div>

        <h2 className={classes.title}>{title}</h2>
        <p className={classes.body}>{t('menu.welcome.body')}</p>

        <button type="button" className={classes.showBtn} onClick={onShowMe}>
          {t('menu.welcome.show')}
          <span className={classes.chevron} aria-hidden="true">›</span>
        </button>

        <button type="button" className={classes.skipBtn} onClick={onSkip}>
          {t('menu.welcome.skip')}
        </button>
      </div>
    </div>
  );
}

export default WelcomePanel;
